import type { PropInstance } from "../document/types.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import type { FrameSize } from "./prop-hit.ts";

/**
 * Where a presence prop counts its viewer as inside when its author paints no cells (ADR-058 amendment,
 * P1.3-T4): the ground under its art, the rectangle its solid art bounds cover from their first row down to
 * the base, as `[column, rowsUp]` collision cells of the frame's own space. The compiler never sees art
 * bounds, since the atlas is built after it, so the game client derives the area from the world's asset
 * manifest with this function and the editor ghosts the same cells behind the Activation section.
 */

/**
 * The collision cells a pixel span covers along one axis: every cell the span touches, so a span that
 * starts or ends between cells takes both.
 *
 * @param from First pixel of the span.
 * @param to The pixel after its last.
 * @param cellPixels Pixels per collision cell.
 * @returns The first and last cell, or null for an empty span.
 */
function cellSpan(from: number, to: number, cellPixels: number): readonly [number, number] | null {
  return to <= from ? null : [Math.floor(from / cellPixels), Math.ceil(to / cellPixels) - 1];
}

/**
 * The cells under a frame's art, mirrored as the art is drawn. Without art bounds the whole width of the
 * frame above its base stands in, as the occluder test takes it (ADR-040).
 *
 * @param size The frame's size, base and art bounds, from the asset manifest.
 * @param cellPixels Pixels per collision cell.
 * @param flipX Whether the prop is mirrored horizontally.
 * @param flipY Whether the prop is mirrored vertically.
 * @returns The cells, row-major from the frame's bottom; empty when the art has no height or width.
 */
export function presenceArtCells(size: FrameSize, cellPixels: number, flipX = false, flipY = false): (readonly [number, number])[] {
  const artX = size.art?.x ?? 0;
  const artW = size.art?.w ?? size.w;
  const artY = size.art?.y ?? 0;
  const left = flipX ? size.w - artX - artW : artX;
  const top = flipY ? size.h - size.base : artY;
  const bottom = flipY ? size.h - artY : size.base;
  const columns = cellSpan(left, left + artW, cellPixels);
  const rows = cellSpan(size.h - bottom, size.h - top, cellPixels);
  const cells: (readonly [number, number])[] = [];
  if (columns === null || rows === null) {
    return cells;
  }
  for (let rowsUp = rows[0]; rowsUp <= rows[1]; rowsUp += 1) {
    for (let column = columns[0]; column <= columns[1]; column += 1) {
      cells.push([column, rowsUp]);
    }
  }
  return cells;
}

/**
 * The part of a strip a presence prop plays (ADR-058 amendment, P1.3-T4): every frame but the first, which
 * is the drawing of the state it leaves and which the prop already shows, with their durations, so the
 * editor previews what the game plays. A strip of a single frame is left as it is: the game cuts past it.
 *
 * @param strip The strip, its frames by frame id with a duration each.
 * @returns The strip from its second frame.
 */
export function presenceStrip<T extends { readonly frames: readonly string[]; readonly durations: readonly number[] }>(strip: T): T {
  return strip.frames.length < 2 ? strip : { ...strip, frames: strip.frames.slice(1), durations: strip.durations.slice(1) };
}

/**
 * The state a placed prop shows while its viewer stands inside (ADR-058 amendment), read from the type the
 * game registers: what the editor draws for every presence prop when the view shows them inside, and what
 * the pick-through modifier passes over (P1.6-T41). Daedalus knows a presence prop only by this
 * registration, never by a type's name.
 *
 * @param types The registered object types.
 * @param prop The placed prop.
 * @returns The inside state's id, or null for a prop without a behaviour, of an unregistered type, or of a
 *   type driven by verbs.
 */
export function presenceInsideOf(types: readonly ObjectTypeDefinition[], prop: PropInstance): string | null {
  const type = prop.behaviour?.type;
  return type === undefined ? null : (types.find((t) => t.type === type)?.presence?.inside ?? null);
}
